import { clockFromTime } from '@am/sim';
import { formatMoney, type Lang, type MessageKey, useT } from '../i18n';
import { canLoad, type SaveMeta, versionNote } from '../saves';
import { useGame } from '../store';
import { Icon } from './icons';

/** How long ago, in words ("3 minutes ago", "yesterday"), in the game's language. */
function ago(savedAt: number, lang: Lang): string {
  const minutes = Math.floor((Date.now() - savedAt) / 60_000);
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
  if (minutes < 1) return rtf.format(0, 'minute');
  if (minutes < 60) return rtf.format(-minutes, 'minute');
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return rtf.format(-hours, 'hour');
  const days = Math.floor(hours / 24);
  if (days < 7) return rtf.format(-days, 'day');
  return new Date(savedAt).toLocaleDateString(lang);
}

/** One saved game in a list: picture, name, game day and time, money, when it was saved. */
export function SaveSummary({ save }: { save: SaveMeta }) {
  const t = useT();
  const lang = useGame((s) => s.lang);
  const c = clockFromTime(save.time);
  const clock = `${String(c.hour).padStart(2, '0')}:${String(c.minute).padStart(2, '0')}`;
  const version = versionNote(save);
  return (
    <span className="save-summary">
      <span className="save-picture">
        {save.picture ? (
          <img className="save-img" src={save.picture} alt="" draggable={false} />
        ) : (
          <Icon name="plane" size={22} />
        )}
      </span>
      <span className="save-text">
        <span className="save-name">
          {save.auto ? t('save.autoName') : save.name}
          {save.auto && <span className="chip">{t('save.auto')}</span>}
          {save.mode === 'coop' && <span className="chip">{t('save.coop')}</span>}
          {(save.theme === 1 || save.theme === 2) && (
            <span className={`chip map-chip map-chip-${save.theme}`}>
              {t(`menu.mapTheme.${save.theme}` as MessageKey)}
            </span>
          )}
        </span>
        <span className="save-facts small">
          <span>{t('hud.day', { day: c.day })}</span>
          <span>{clock}</span>
          <span className={save.money < 0 ? 'bad' : ''}>{formatMoney(save.money, lang)}</span>
        </span>
        <span className="save-when small muted">
          {ago(save.savedAt, lang)}
          {version && (
            <span className={canLoad(save) ? '' : 'bad'}>
              {' · '}
              {canLoad(save) ? t('save.olderVersion', { v: version }) : t('save.newerVersion')}
            </span>
          )}
        </span>
      </span>
    </span>
  );
}
